import type { ApiClient } from './ApiClient';

// STU-02 — formes alignées sur les routes de apps/api/src/routes/studentAccounts.ts
// (côté ÉCOLE et côté PARENT) — seule source de vérité. Un compte élève n'est
// JAMAIS un `User` : ces routes n'exposent ni mot de passe, ni jeton.
export type StudentAccountStatus = 'PENDING' | 'ACTIVE';

export interface StudentAccount {
  id: string;
  username: string;
  status: StudentAccountStatus;
  activatedAt: string | null;
  createdAt: string;
}

// ─── Côté école (SCHOOL_ADMIN) ────────────────────────────────────────────────

export interface GetSchoolStudentAccountResponse {
  // Niveau courant (dernière inscription APPROVED) dans le cycle Collège.
  eligible: boolean;
  account: StudentAccount | null;
}

export function getSchoolStudentAccount(
  client: ApiClient,
  tenantId: string,
  studentId: string,
  accessToken: string,
): Promise<GetSchoolStudentAccountResponse> {
  return client.get<GetSchoolStudentAccountResponse>(`/schools/${tenantId}/students/${studentId}/student-account`, {
    accessToken,
  });
}

export interface StudentAccountResponse {
  account: StudentAccount;
}

// ⚠️ La réponse ne contient JAMAIS le lien d'activation : il part par email au
// parent, l'école ne le voit pas.
export function createStudentAccount(
  client: ApiClient,
  tenantId: string,
  studentId: string,
  accessToken: string,
  payload: { username?: string } = {},
): Promise<StudentAccountResponse> {
  return client.post<StudentAccountResponse>(
    `/schools/${tenantId}/students/${studentId}/student-account`,
    payload,
    { accessToken },
  );
}

export function resendStudentAccountActivation(
  client: ApiClient,
  tenantId: string,
  studentId: string,
  accessToken: string,
): Promise<StudentAccountResponse> {
  return client.post<StudentAccountResponse>(
    `/schools/${tenantId}/students/${studentId}/student-account/resend-activation`,
    {},
    { accessToken },
  );
}

// ─── Côté parent (titulaire ou co-parent PAR-04 accepté) ─────────────────────

export interface GetChildStudentAccountResponse {
  account: StudentAccount | null;
}

export function getChildStudentAccount(
  client: ApiClient,
  studentId: string,
  accessToken: string,
): Promise<GetChildStudentAccountResponse> {
  return client.get<GetChildStudentAccountResponse>(`/students/${studentId}/student-account`, { accessToken });
}

export interface StudentAccessLinkResponse {
  // ACTIVATION tant que le compte n'a jamais été activé, RESET ensuite.
  kind: 'ACTIVATION' | 'RESET';
  link: string;
  expiresAt: string;
  account: StudentAccount;
}

// Génère un NOUVEAU lien (l'ancien lien non utilisé est invalidé) — aussi
// envoyé par email au parent qui le demande.
export function createChildStudentAccessLink(
  client: ApiClient,
  studentId: string,
  accessToken: string,
): Promise<StudentAccessLinkResponse> {
  return client.post<StudentAccessLinkResponse>(`/students/${studentId}/student-account/access-link`, {}, {
    accessToken,
  });
}
